import React, { useState } from 'react';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../../Enums/MagicField.enum';
import useBillingAddressContainer from '../../../../../../Components/Manager/Hooks/useBillingAddressContainer';
import usePatientAddressContainer from '../../../../../EArchive/Hooks/usePatientAddressContainer';
import { useEffectAsync } from '../../../../../../helpers';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import {
	Meta,
	useLazyLaboratorySetupCompanyDataQuery,
} from '../../../../../../graphql/graphql';
import useMeta from '../../../../../Meta/Hooks/useMeta';
import useLoading from '../../../../../Base/Hooks/useLoading';
import { SelectValue } from '../../../../../../Components/Base/Select/Select';

export interface LaboratoryActivationCompanyDataValues {
	companyName: string;
	identificationNumber: string;
	workplaceName: string;
	workplaceFiledOfCare: SelectValue;
	icp?: string;
	address: {
		street: string;
		descriptiveNumber: string;
		guideNumber: string;
		city: string;
		postCode: string;
	};
	workplaceContactPhone: string;
	workplaceContactEmail: string;
	isAddressUsedAsBillingAddress: boolean;
	billingAddress?: {
		street: string;
		descriptiveNumber: string;
		guideNumber: string;
		city: string;
		postCode: string;
	};
}

interface Props {
	workplaceId: string;
	onSuccess: (values: LaboratoryActivationCompanyDataValues) => void;
}

const defaultFormScheme: ItemScheme[] = [
	{
		type: MagicField.TEXT_FIELD,
		key: 'companyName',
		title: 'component.laboratoryActivationCompanyData.companyName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'identificationNumber',
		title: 'component.laboratoryActivationCompanyData.identificationNumber',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'workplaceName',
		title: 'component.laboratoryActivationCompanyData.workplaceName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.FIELD_OF_CARE_SUKL_SELECT,
		key: 'workplaceFiledOfCare',
		title: 'component.laboratoryActivationCompanyData.workplaceFiledOfCare',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'icp',
		title: 'component.laboratoryActivationCompanyData.icp',
	},
	{
		type: MagicField.TEXT,
		subType: 'header',
		title: 'component.laboratoryActivationCompanyData.workplaceAddress',
	},
	{
		type: MagicField.CONTAINER,
		key: 'address',
		children: [],
	},
	{
		type: MagicField.PHONE_NUMBER,
		key: 'workplaceContactPhone',
		title: 'component.laboratoryActivationCompanyData.workplaceContactPhone',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.EMAIL,
		key: 'workplaceContactEmail',
		title: 'component.laboratoryActivationCompanyData.workplaceContactEmail',
		settings: {
			required: false,
		},
	},
	{
		type: MagicField.CHECKBOX,
		key: 'isAddressUsedAsBillingAddress',
		title:
			'component.laboratoryActivationCompanyData.isAddressUsedAsBillingAddress',
	},
	{
		type: MagicField.CONDITION,
		subType: 'view',
		settings: {
			rules: [
				{
					formControlKey: 'isAddressUsedAsBillingAddress',
					rule: 'equal',
					value: false,
				},
			],
		},
		children: [
			{
				type: MagicField.TEXT,
				subType: 'header',
				title: 'component.laboratoryActivationCompanyData.billingAddress',
			},
			{
				type: MagicField.CONTAINER,
				key: 'billingAddress',
				children: [],
			},
		],
	},
];

const LaboratoryActivationCompanyData: React.FC<Props> = ({
	workplaceId,
	onSuccess,
}) => {
	const [formScheme, setFormScheme] = useState<ItemScheme[]>([]);
	const [defaultValues, setDefaultValues] = useState({});
	const { addToAddressContainer } = usePatientAddressContainer();
	const { addToBillingAddressContainer } = useBillingAddressContainer();
	const { displayGQLError } = useApiClient();
	const [getWorkplaceData] = useLazyLaboratorySetupCompanyDataQuery();
	const { getMetaAsObject } = useMeta();
	const { setIsLoading, RenderLoading, isLoading } = useLoading(true);

	useEffectAsync(async () => {
		setIsLoading(true);
		const schemeWithAddress = await addToAddressContainer(defaultFormScheme);
		const schemeWithBillingAddress = await addToBillingAddressContainer(
			schemeWithAddress,
		);
		setFormScheme(schemeWithBillingAddress);
		setIsLoading(false);
	}, []);

	useEffectAsync(async () => {
		setIsLoading(true);
		const response = await getWorkplaceData({ id: workplaceId });
		displayGQLError(response);
		if ('data' in response) {
			const workplaceData = response.data?.workplace;
			if (workplaceData) {
				const meta = getMetaAsObject(workplaceData.meta as Meta[]);
				setDefaultValues({
					companyName: workplaceData?.rootWorkplace?.name,
					identificationNumber:
						workplaceData?.rootWorkplace?.billingAddress?.identificationNumber,
					workplaceName: workplaceData?.name,
					workplaceFiledOfCare: workplaceData.termItems?.find(
						item => item?.term?.name === 'field_of_care',
					)?.id,
					icp: meta['icp'],
					address: workplaceData?.address,
					workplaceContactPhone: workplaceData?.phone,
					isAddressUsedAsBillingAddress: true,
				});
			}
		}
		setIsLoading(false);
	}, []);

	const handleSubmit = (values: LaboratoryActivationCompanyDataValues) => {
		onSuccess(values);
	};

	if (isLoading) return <RenderLoading />;

	return (
		<MagicForm
			defaultValues={defaultValues}
			formScheme={formScheme}
			onSubmit={handleSubmit}
			submitText='base.shared.continue'
		/>
	);
};

export default LaboratoryActivationCompanyData;
